import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  Search,
  PenTool,
  Code2,
  Rocket,
  Target,
  RefreshCw,
  Shield,
} from "lucide-react"
import { Link } from "react-router-dom"
import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { methodologyFAQs } from "@/data/faqData"

/* ───────────────────────────── DATA ───────────────────────────── */

const principles = [
  {
    icon: Target,
    title: "Client-Centric",
    description:
      "Your business goals are our north star. Every technical decision is aligned with your strategic objectives and measurable outcomes.",
  },
  {
    icon: RefreshCw,
    title: "Agile & Iterative",
    description:
      "We deliver value in sprints, allowing for rapid feedback and course correction. No waterfall. No surprises.",
  },
  {
    icon: Shield,
    title: "Future-Proof",
    description:
      "We build with scalability in mind, using modern architectures and AI-native patterns that grow with your business.",
  },
]

const steps = [
  {
    num: "01",
    icon: Search,
    title: "Discovery & Strategy",
    description:
      "We start by deep-diving into your business. We analyze your pain points, market position, and goals to craft a comprehensive digital roadmap.",
    points: [
      "Stakeholder Interviews",
      "Market Analysis",
      "Technical Feasibility Study",
      "Roadmap Definition",
    ],
  },
  {
    num: "02",
    icon: PenTool,
    title: "Design & User Experience",
    description:
      "We translate strategy into visual interfaces. Our design process ensures the solution is intuitive, engaging, and accessible.",
    points: [
      "Initial Wireframing",
      "UI/UX Design",
      "Interactive Prototyping",
      "User Testing",
    ],
  },
  {
    num: "03",
    icon: Code2,
    title: "Engineering & Development",
    description:
      "Our expert engineers bring designs to life using cutting-edge technologies. We follow best practices for clean, maintainable, and secure code.",
    points: [
      "Agile Sprints",
      "Full-Stack Development",
      "Code Reviews",
      "Continuous Integration",
    ],
  },
  {
    num: "04",
    icon: Rocket,
    title: "Launch & Evolution",
    description:
      "We deploy your solution with confidence. But launch is just the beginning — we monitor, optimize, and iterate to ensure long-term success.",
    points: [
      "QA & Testing",
      "Deployment Automation",
      "Performance Monitoring",
      "Ongoing Support",
    ],
  },
]

/* ───────────────────────────── PAGE ───────────────────────────── */

export default function Methodology() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <SEO
        title="Our Methodology | Engineering Outcomes | RSA Tech Group"
        description="We don't just write code, we rigorously engineer outcomes. Our iterative process ensures high-impact, future-proof digital solutions."
      />
      <Navigation />

      {/* ═══════════════════════════════════════════════════════════
          S1  HERO — Dark
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="dark" className="relative pt-40 pb-24 overflow-hidden bg-[#0f1629]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(28,43,110,0.5),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(28,43,110,0.3),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_40%,rgba(253,164,0,0.08),transparent_70%)]" />

        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#FDA400]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1C2B6E]/[0.15] rounded-full blur-[120px]" />

        <div className="section-container relative z-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#FDA400]" />
            <span className="text-[#FDA400] text-xs font-semibold uppercase tracking-[0.2em]">
              Our Methodology
            </span>
            <div className="h-px w-10 bg-[#FDA400]" />
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6 max-w-4xl mx-auto">
            We Don't Just Write Code.{" "}
            <br className="hidden md:block" />
            We <span className="text-[#FDA400]">Engineer Outcomes.</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 leading-relaxed font-light max-w-2xl mx-auto">
            Our relentless, iterative process proactively identifies opportunities and risks, ensuring we deliver high-impact, future-proof digital solutions that scale without compromise.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S2  CORE PRINCIPLES — Light + Dot Grid
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="light" className="relative py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">
                Our Approach
              </span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
              The RSA Tech Group{" "}
              <span className="text-[#FDA400]">Advantage</span>
            </h2>
            <p className="text-lg text-slate-500 max-w-3xl mx-auto mt-4 font-light">
              Our methodology is rooted in Agile principles but enhanced for the AI era. We prioritize speed, adaptability, and continuous value delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {principles.map((item) => (
              <div
                key={item.title}
                className="group partner-card relative rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
              >
                {/* Top accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                {/* Glow */}
                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-[#1C2B6E]/[0.08] flex items-center justify-center mb-6 group-hover:bg-[#1C2B6E] transition-colors duration-300">
                    <item.icon className="w-6 h-6 text-[#1C2B6E] group-hover:text-[#FDA400] transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1C2B6E] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S3  HOW WE DELIVER — Dark + Glassmorphic Steps
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="dark" className="relative py-24 bg-[#0f1629] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(253,164,0,0.04),transparent_70%)]" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#1C2B6E]/[0.15] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <p className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.2em] mb-4">
              How We Deliver
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-white">
              From Discovery to{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FDA400] to-[#FDB944]">
                Launch
              </span>
            </h2>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {steps.map((step) => (
              <div
                key={step.num}
                className="group relative rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] hover:border-[#FDA400]/20 overflow-hidden transition-all duration-500 hover:-translate-y-1"
              >
                {/* Ghost number */}
                <div className="absolute top-4 right-6 text-[80px] font-black text-white/[0.03] leading-none select-none pointer-events-none">
                  {step.num}
                </div>

                {/* Shine sweep on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
                  <div className="absolute inset-0 animate-[shine_1.5s_ease-in-out]" style={{ background: "linear-gradient(105deg, transparent 40%, rgba(253,164,0,0.06) 45%, rgba(253,164,0,0.12) 50%, rgba(253,164,0,0.06) 55%, transparent 60%)" }} />
                </div>

                {/* Glow */}
                <div className="absolute -bottom-8 -right-8 w-28 h-28 rounded-full bg-[#FDA400]/0 group-hover:bg-[#FDA400]/10 blur-[60px] transition-all duration-700" />

                <div className="relative z-10 p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
                  {/* Icon + Number */}
                  <div className="flex items-center gap-4 md:flex-col md:items-center md:gap-2 flex-shrink-0">
                    <div className="w-14 h-14 rounded-2xl bg-white/[0.06] flex items-center justify-center group-hover:bg-[#FDA400]/10 transition-colors duration-300">
                      <step.icon className="w-6 h-6 text-[#FDA400]" />
                    </div>
                    <span className="text-sm font-bold text-[#FDA400] md:text-center">
                      Step {step.num}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed text-[15px] mb-5">
                      {step.description}
                    </p>

                    {/* Points */}
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                      {step.points.map((point) => (
                        <div
                          key={point}
                          className="flex items-center gap-2 text-sm text-slate-500"
                        >
                          <div className="h-1.5 w-1.5 rounded-full bg-[#FDA400] flex-shrink-0" />
                          {point}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S4  FAQ — Light
      ═══════════════════════════════════════════════════════════ */}
      <PageFAQ
        faqs={methodologyFAQs}
        title="Methodology FAQs"
        subtitle="Common questions about our delivery approach and process"
        variant="muted"
      />

      {/* ═══════════════════════════════════════════════════════════
          S5  CTA — Dark
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="dark" className="relative py-24 bg-[#0f1629] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(253,164,0,0.08),transparent_70%)]" />
        <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#1C2B6E]/[0.2] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="relative rounded-3xl bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] p-10 md:p-16 overflow-hidden">
              {/* Inner glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] bg-[#FDA400]/[0.06] rounded-full blur-[80px]" />

              <div className="relative z-10">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight">
                  Ready to Start
                  <br /> Your <span className="text-[#FDA400]">Journey?</span>
                </h2>
                <p className="text-lg text-slate-400 max-w-xl mx-auto mb-10">
                  Let's work together to build something extraordinary. Start with a free consultation.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#FDA400] text-[#1C2B6E] px-8 py-4 rounded-full font-bold text-base hover:bg-[#FDB944] transition-colors"
                >
                  Get in Touch <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
